// The join page (14-viewers-join.md, "The join page"): what a channel or space the person isn't in
// is, from the client or the room's summary, and the one way on: Join, Accept or Decline an
// invitation, or a plain line with "Go to Home". After joining it opens by itself (the "View" step
// was a candidate to drop).
import { EventType, JoinRule, type MatrixClient, type Room } from 'matrix-js-sdk';
import { listDirect } from '../people/direct';
import { useEffect, useState } from 'react';
import { Avatar } from '../blocks/Bits';
import { Button } from '../blocks/Button';
import { Icon } from '../blocks/icons';
import { LoadState, NO_ANSWER_MS, Reference, useLoad } from '../blocks/Wait';
import { describe, type Failure } from '../core/errors';
import { formatCount } from '../core/format';
import { navigate } from '../core/router';
import { serverOf } from '../core/session';
import { t } from '../i18n';
import { thumbnail } from '../session/media';
import { findRoom } from '../session/rooms';
import { nameIn } from '../chat/events';
import { chatIcons } from '../chat/icons';
import { TopicViewer } from '../chat/viewers';
import { spacePath } from './menus';

type Joining = { state: 'idle' } | { state: 'busy' } | { state: 'slow' } | { state: 'failed'; failure: Failure };

export function JoinPage({ client, refName }: { client: MatrixClient; refName: string }) {
  const me = client.getUserId()!;
  const via = new URLSearchParams(location.search).getAll('viaServers');
  const known: Room | undefined = findRoom(client, refName) ?? client.getRoom(refName) ?? undefined;
  const membership = known?.getMyMembership();
  const ownEvent = known?.getMember(me)?.events.member;
  // Another company's: an address (or an older room ID) naming another server; newer IDs name none.
  const foreign = refName.includes(':') && serverOf(refName) !== serverOf(me) && !known;
  const [summary, retrySummary] = useLoad(() => (foreign ? Promise.resolve(undefined) : client.getRoomSummary(refName, via)), [refName]);
  const found = summary.state === 'done' ? summary.value : undefined;
  const [joining, setJoining] = useState<Joining>({ state: 'idle' });
  const [joinedId, setJoinedId] = useState<string>();
  const [topicOpen, setTopicOpen] = useState(false);

  // Once the joined room is in the lists it opens: a channel in place, a space at its lobby.
  useEffect(() => {
    const room = joinedId ? client.getRoom(joinedId) : undefined;
    if (room?.getMyMembership() === 'join' && room.isSpaceRoom()) navigate(spacePath(room), { replace: true });
  });

  const name = known?.name ?? found?.name ?? (refName.startsWith('#') ? refName.slice(1).split(':')[0] : t('join.channel'));
  const topic: string | undefined = known?.currentState.getStateEvents(EventType.RoomTopic, '')?.getContent().topic ?? found?.topic;
  const members = known?.getJoinedMemberCount() || found?.num_joined_members;
  const space = known?.isSpaceRoom() || found?.room_type === 'm.space';
  const picture = known?.getMxcAvatarUrl() ?? found?.avatar_url;

  /** One request; after 40 s without an answer Join is usable again (a late success still opens). */
  const join = async () => {
    setJoining({ state: 'busy' });
    const slow = setTimeout(() => setJoining((j) => (j.state === 'busy' ? { state: 'slow' } : j)), NO_ANSWER_MS);
    try {
      const roomId = (await client.joinRoom(refName, { viaServers: via })).roomId;
      // An accepted direct-chat invitation is listed under the inviter (20, Invites).
      if (ownEvent?.getContent().is_direct && membership === 'invite') await listDirect(client, roomId, ownEvent.getSender()!).catch(() => {});
      setJoinedId(roomId);
    } catch (e) {
      setJoining({ state: 'failed', failure: describe(e, 'join') });
    } finally {
      clearTimeout(slow);
    }
  };
  const decline = async () => {
    setJoining({ state: 'busy' });
    await client.leave(known!.roomId).then(
      () => navigate('/home/', { replace: true }),
      (e) => setJoining({ state: 'failed', failure: describe(e, 'general') }),
    );
  };

  const goHome = (
    <Button small onClick={() => navigate('/home/')}>
      {t('common.goHome')}
    </Button>
  );
  const busy = joining.state === 'busy';
  let action;
  if (membership === 'invite') {
    const inviter = ownEvent?.getSender() ?? '';
    const direct = !!ownEvent?.getContent().is_direct;
    action = (
      <>
        <p>{t(direct ? 'join.wantsToChat' : 'join.invitedYou', { name: nameIn(known!, inviter) })}</p>
        <div className="join-buttons">
          <Button small variant="filled" busy={busy} onClick={() => void join()}>
            {t('join.accept')}
          </Button>
          <Button small disabled={busy} onClick={() => void decline()}>
            {t('join.decline')}
          </Button>
        </div>
      </>
    );
  } else if (membership === 'leave' || membership === 'ban') {
    const removed = membership === 'ban' || (ownEvent && ownEvent.getSender() !== me);
    const inviteOnly = known!.getJoinRule() === JoinRule.Invite;
    action = removed ? (
      <>
        <p>{t('join.removed')}</p>
        {goHome}
      </>
    ) : (
      <>
        <p>{t('join.left')}</p>
        {inviteOnly ? (
          <>
            <p>{t('join.askAgain')}</p>
            {goHome}
          </>
        ) : null}
      </>
    );
    if (!removed && !inviteOnly) action = <>{action}{joinButton()}</>;
  } else if (foreign) {
    action = (
      <>
        <p>{t('join.otherCompany')}</p>
        {goHome}
      </>
    );
  } else if (summary.state === 'failed' && summary.failure.kind !== 'NW') {
    action = (
      <>
        <p>{t('join.nowhere')}</p>
        {goHome}
      </>
    );
  } else if (summary.state !== 'done') {
    action = <LoadState status={summary} retry={retrySummary} compact />;
  } else if (found?.join_rule === JoinRule.Invite || found?.join_rule === JoinRule.Knock) {
    action = <p>{t('error.joinInviteOnly')}</p>;
  } else action = joinButton();

  function joinButton() {
    return (
      <>
        <Button small busy={busy} onClick={() => void join()}>
          {busy ? t('join.joining') : t('join.join')}
        </Button>
        {joining.state === 'slow' && <p className="join-line">{t('wait.noAnswer')}</p>}
        {joining.state === 'failed' && (
          <div className="join-error" role="alert" data-desyr-join-error="">
            <p>{joining.failure.text}</p>
            {joining.failure.reference && <Reference code={joining.failure.reference} />}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="join-page">
      <div className="join-card">
        <div className="join-top">
          <Avatar id={known?.roomId ?? refName} name={name} src={thumbnail(client, picture, 48)} size={64} round={false} />
          {space && <span className="file-type">{t('join.space')}</span>}
        </div>
        <h2 className="join-name">{name}</h2>
        {topic && (
          <button type="button" className="join-topic" onClick={() => setTopicOpen(true)}>
            {topic}
          </button>
        )}
        {members ? (
          <p className="join-members">
            <Icon svg={chatIcons.person} size={16} /> {t('join.members', { n: members, count: formatCount(members) })}
          </p>
        ) : null}
        {action}
      </div>
      {topicOpen && topic && <TopicViewer name={name} topic={topic} onClose={() => setTopicOpen(false)} />}
    </div>
  );
}
